import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { PAYMENT_ICONS } from '../constants/paymentIcons';

const PaymentMethodsPage = ({ user, onLogout }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [enabledMethods, setEnabledMethods] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMethod, setNewMethod] = useState({
    type: '',
    account_number: '',
    name: '',
    is_default: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentMethods();
    fetchEnabledMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnabledMethods = async () => {
    try {
      const response = await axios.get(`${API}/payout-methods/enabled`);
      setEnabledMethods(response.data.enabled_payout_methods || {});
    } catch (error) {
      console.error('Error fetching enabled methods:', error);
    }
  };

  // Get list of enabled method keys for dropdown
  const enabledMethodOptions = Object.entries(enabledMethods)
    .filter(([_, enabled]) => enabled)
    .map(([key]) => key);

  const handleAddMethod = async () => {
    try {
      await axios.post(`${API}/payment-methods`, {
        type: newMethod.type,
        name: newMethod.name,
        account_number: newMethod.account_number,
        is_default: newMethod.is_default
      });
      setShowAddModal(false);
      setNewMethod({
        type: '',
        account_number: '',
        name: '',
        is_default: false
      });
      fetchPaymentMethods();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to add payment method');
    }
  };

  const handleDeleteMethod = async (methodId) => {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }
    try {
      await axios.delete(`${API}/payment-methods/${methodId}`);
      fetchPaymentMethods();
    } catch (error) {
      alert('Failed to delete payment method');
    }
  };

  const getMethodIcon = (type) => {
    const iconUrl = PAYMENT_ICONS[type] || PAYMENT_ICONS.bank_account;
    return <img src={iconUrl} alt={type} className="w-10 h-10 object-contain rounded" />;
  };

  const getMethodName = (type) => {
    switch(type) {
      case 'gcash': return 'GCash';
      case 'maya': return 'Maya (PayMaya)';
      case 'bank_transfer': return 'Bank Transfer';
      case 'card': return 'Credit/Debit Card';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-methods-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
            Payment Methods
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
            data-testid="add-payment-method-button"
          >
            + Add Payment Method
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="text-2xl">ℹ️</div>
            <div>
              <h3 className="font-bold mb-2">Supported Payment Methods</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                {enabledMethodOptions.length > 0 ? (
                  enabledMethodOptions.map(key => (
                    <li key={key}><strong>{getMethodName(key)}:</strong> {key === 'gcash' ? 'Popular mobile wallet in the Philippines' : key === 'maya' ? 'Digital payment platform' : key === 'bank_transfer' ? 'Direct bank account transfer' : key === 'card' ? 'Visa, Mastercard, and other card payments' : ''}</li>
                  ))
                ) : (
                  <li>Loading supported methods...</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Payment Methods List */}
        {paymentMethods.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 mb-4">No payment methods added yet</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="text-blue-600 hover:underline font-semibold"
            >
              Add your first payment method
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition"
                data-testid={`payment-method-${method.id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-4">
                    <div>{getMethodIcon(method.type || method.method_type)}</div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-bold">{getMethodName(method.type || method.method_type)}</h3>
                        {method.is_default && (
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700">
                        <strong>Account Name:</strong> {method.name || method.account_name}
                      </p>
                      <p className="text-gray-700">
                        <strong>Account Number:</strong> {method.account_number}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Added on {new Date(method.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteMethod(method.id)}
                    className="text-red-600 hover:text-red-700 font-semibold text-sm"
                    data-testid={`delete-method-${method.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Payment Method Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold mb-4">Add Payment Method</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Payment Type</label>
                <select
                  value={newMethod.type}
                  onChange={(e) => setNewMethod({...newMethod, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="method-type-select"
                >
                  <option value="">Select payment type</option>
                  {enabledMethodOptions.map(key => (
                    <option key={key} value={key}>{getMethodName(key)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Account Name</label>
                <input
                  type="text"
                  value={newMethod.name}
                  onChange={(e) => setNewMethod({...newMethod, name: e.target.value})}
                  placeholder="Full name as registered"
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="account-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  {newMethod.type === 'bank_transfer' ? 'Account Number' : 'Mobile Number'}
                </label>
                <input
                  type="text"
                  value={newMethod.account_number}
                  onChange={(e) => setNewMethod({...newMethod, account_number: e.target.value})}
                  placeholder={newMethod.type === 'bank_transfer' ? '1234567890' : '09171234567'}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="account-number-input"
                />
              </div>

              {newMethod.type === 'bank_transfer' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">Bank Name</label>
                  <input
                    type="text"
                    value={newMethod.bank_name || ''}
                    onChange={(e) => setNewMethod({...newMethod, bank_name: e.target.value})}
                    placeholder="e.g. BDO, BPI, Metrobank"
                    className="w-full px-3 py-2 border rounded-lg"
                    data-testid="bank-name-input"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={newMethod.is_default}
                  onChange={(e) => setNewMethod({...newMethod, is_default: e.target.checked})}
                  className="mr-2"
                  data-testid="default-checkbox"
                />
                <label htmlFor="is_default" className="text-sm">Set as default payment method</label>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-200 py-2 rounded-lg hover:bg-gray-300"
                  data-testid="cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMethod}
                  disabled={!newMethod.type}
                  className={`flex-1 py-2 rounded-lg ${newMethod.type ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-lg' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                  data-testid="save-button"
                >
                  Add Method
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PaymentMethodsPage;
